<template>
  <div class="w-full h-full flex flex-col">
    <div class="w-full flex-1 min-h-0" @mouseleave="onChartMouseLeave">
      <component
        v-if="chartConfig && chartConfig.type"
        :is="chartConfig.type"
        :data="translatedChartData"
        :options="chartOptions"
        class="w-full h-full"
      />
      <div v-else class="flex items-center justify-center h-full text-gray-500">
        {{ $t('LineChartComponent.noDataAvailable') || 'No data available' }}
      </div>
    </div>
  </div>
</template>

<script>
import { Bar, Line } from 'vue-chartjs';
import 'chartjs-adapter-date-fns';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
  TimeScale,
  Filler,
} from 'chart.js';
import {
  formatLocaleDate,
  isIsoDateChartLabel,
} from '@/helpers/formatLocaleDate';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
  TimeScale,
  Filler,
);

const verticalLinePlugin = {
  id: 'verticalLine',
  afterDraw(chart) {
    const selectedIdx = chart.options?.plugins?.verticalLine?.selectedIndex;
    if (selectedIdx == null || selectedIdx < 0) return;
    const meta = chart.getDatasetMeta(0);
    if (!meta?.data?.[selectedIdx]) return;
    const x = meta.data[selectedIdx].x;
    const { top, bottom } = chart.chartArea;
    const { ctx } = chart;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = 'rgba(37, 99, 235, 0.4)';
    ctx.setLineDash([4, 4]);
    ctx.stroke();
    ctx.restore();
  },
};

ChartJS.register(verticalLinePlugin);

export default {
  name: 'LineChartComponent',
  components: {
    Bar,
    Line,
  },
  emits: ['label-hover', 'label-hover-leave', 'label-click'],
  props: {
    chartConfig: {
      type: Object,
      required: true,
    },
    dimensions: {
      type: Array,
      required: true,
    },
    selectedLabel: {
      type: String,
      default: null,
    },
    stickyAnswerCounts: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      windowWidth: window.innerWidth,
      lastEmittedLabel: null,
    };
  },
  created() {
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  watch: {
    chartConfig: {
      handler() {
      },
      deep: true,
      immediate: true,
    },
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768;
    },
    useLikertScale() {
      const y = this.chartConfig?.options?.scales?.y;
      return y && y.min === 0 && (y.max === 6 || y.max === 5);
    },
    /** For Likert line charts: use 0–4 scale (Strongly disagree = 0), scale -1 to 5 so line has room. */
    useLikertLineScale() {
      return this.chartConfig?.type === 'line' && this.useLikertScale;
    },
    /** On mobile with Likert scale: show numeric y-axis (1–5) and legend below. */
    showMobileLikertLegend() {
      return this.isMobile && this.useLikertScale;
    },
    selectedIndex() {
      if (!this.selectedLabel) return -1;
      const labels = this.chartConfig?.data?.labels || [];
      const labelIdx = labels.indexOf(this.selectedLabel);
      if (labelIdx >= 0) return labelIdx;
      const ds = this.chartConfig?.data?.datasets?.[0];
      if (!ds?.data) return -1;
      return ds.data.findIndex((point) => {
        if (point == null || typeof point !== 'object') return false;
        return String(point.x) === String(this.selectedLabel);
      });
    },
    translatedChartData() {
      return this.getTranslatedChartData(this.chartConfig);
    },
    chartOptions() {
      return {
        ...this.chartConfig.options,
        plugins: {
          ...this.chartConfig.options?.plugins,
          legend: {
            display: true,
            position: this.isMobile ? 'bottom' : 'top',
            labels: {
              boxWidth: this.isMobile ? 12 : 15,
              padding: this.isMobile ? 8 : 12,
              font: {
                size: this.isMobile ? 10 : 12,
              },
            },
          },
          verticalLine: {
            selectedIndex: this.selectedIndex,
          },
          tooltip: {
            enabled: !this.isMobile,
            callbacks: {
              title: (context) => {
                const idx = context[0]?.dataIndex;
                const datasetIndex = context[0]?.datasetIndex ?? 0;
                const rawLabel = this.getChartRawLabelAtIndex(idx, datasetIndex);
                return this.formatWeekTooltipTitle(rawLabel || context[0]?.label);
              },
              label: (context) => {
                const value = context.parsed?.y;
                const datasetLabel = context.dataset?.label || '';
                if (this.useLikertScale) {
                  const formatted = this.formatLikertTooltipValue(value);
                  return datasetLabel ? `${datasetLabel}: ${formatted}` : formatted;
                }
                if (value == null || Number.isNaN(Number(value))) {
                  const s = value == null ? '' : String(value);
                  return datasetLabel ? `${datasetLabel}: ${s}` : s;
                }
                const formatted = Number(value).toFixed(2);
                return datasetLabel ? `${datasetLabel}: ${formatted}` : formatted;
              },
              afterBody: (context) => {
                if (!context.length) return '';
                const idx = context[0].dataIndex;
                const datasetIndex = context[0].datasetIndex ?? 0;
                const rawLabel = this.getChartRawLabelAtIndex(idx, datasetIndex);
                const count = rawLabel && this.stickyAnswerCounts[rawLabel];
                if (count > 0) {
                  return `\n${this.$t('LineChartComponent.clickForDetails')
                    || 'Answers available \u2013 click for details.'}`;
                }
                return '';
              },
            },
          },
        },
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          ...this.chartConfig.options?.scales,
          x: {
            ...this.chartConfig.options?.scales?.x,
            ticks: {
              ...this.chartConfig.options?.scales?.x?.ticks,
              maxRotation: 0,
              minRotation: 0,
              font: {
                size: this.isMobile ? 8 : 11,
                lineHeight: this.isMobile ? 1.2 : 1.4,
              },
              maxTicksLimit: this.isMobile ? 6 : 10,
            },
            grid: {
              ...this.chartConfig.options?.scales?.x?.grid,
              display: !this.isMobile,
            },
          },
          y: {
            ...this.chartConfig.options?.scales?.y,
            ...(this.useLikertLineScale ? { min: -1, max: 5 } : {}),
            ticks: {
              ...this.chartConfig.options?.scales?.y?.ticks,
              font: {
                size: this.isMobile ? 9 : 11,
              },
              callback: this.useLikertScale
                ? (value) => {
                  const v = Number(value);
                  if (v === -1) return '';
                  // Mobile + Likert: show only numbers 1–5 on y-axis (legend below explains scale)
                  if (this.showMobileLikertLegend) {
                    if (this.useLikertLineScale && v >= 0 && v <= 4 && Number.isInteger(v)) {
                      return String(v + 1);
                    }
                    // Non–0–4 scale: show 1–4; show 5 only once (avoid duplicate when scale max is 5)
                    if (v >= 1 && v <= 4 && Number.isInteger(v)) return String(v);
                    if (v === 5 && Number.isInteger(v) && !this.useLikertLineScale) return '5';
                    return '';
                  }
                  if (this.useLikertLineScale) {
                    if (v >= 0 && v <= 4 && Number.isInteger(v)) return this.getLikertYAxisLabel(v);
                  } else if (v >= 1 && v <= 5 && Number.isInteger(v)) {
                    return this.getLikertYAxisLabel(v);
                  }
                  return '';
                }
                : this.chartConfig.options?.scales?.y?.ticks?.callback,
            },
          },
        },
        onHover: (event, activeElements, chart) => {
          if (this.isMobile) return;
          if (activeElements.length > 0) {
            const idx = activeElements[0].index;
            const datasetIndex = activeElements[0].datasetIndex ?? 0;
            const rawLabel = this.getChartRawLabelAtIndex(idx, datasetIndex);
            if (rawLabel && rawLabel !== this.lastEmittedLabel) {
              this.lastEmittedLabel = rawLabel;
              this.$emit('label-hover', rawLabel);
            }
            chart.canvas.style.cursor = 'pointer';
          } else {
            chart.canvas.style.cursor = 'default';
          }
        },
        onClick: (event, activeElements) => {
          if (this.isMobile) return;
          if (activeElements.length > 0) {
            const idx = activeElements[0].index;
            const datasetIndex = activeElements[0].datasetIndex ?? 0;
            const rawLabel = this.getChartRawLabelAtIndex(idx, datasetIndex);
            if (rawLabel) {
              this.$emit('label-click', rawLabel);
            }
          }
        },
        interaction: {
          intersect: false,
          mode: 'index',
        },
        layout: {
          padding: {
            bottom: this.isMobile ? 20 : 10,
          },
        },
        elements: {
          point: {
            radius: (ctx) => {
              if (this.selectedIndex >= 0 && ctx.dataIndex === this.selectedIndex) {
                return this.isMobile ? 5 : 7;
              }
              return this.isMobile ? 3 : 4;
            },
            hoverRadius: this.isMobile ? 5 : 6,
            borderWidth: (ctx) => {
              if (this.selectedIndex >= 0 && ctx.dataIndex === this.selectedIndex) {
                return 3;
              }
              return 1;
            },
            borderColor: (ctx) => {
              if (this.selectedIndex >= 0 && ctx.dataIndex === this.selectedIndex) {
                return '#1d4ed8';
              }
              return ctx.dataset?.borderColor || '#666';
            },
          },
          line: {
            borderWidth: this.isMobile ? 2 : 3,
          },
        },
      };
    },
  },
  methods: {
    /**
     * Handle window resize to update mobile detection
     */
    handleResize() {
      this.windowWidth = window.innerWidth;
    },

    onChartMouseLeave() {
      if (this.lastEmittedLabel !== null) {
        this.lastEmittedLabel = null;
        this.$emit('label-hover-leave');
      }
    },

    /**
     * Truncate label for mobile display
     * @param {any} label - The label to truncate
     * @returns {string} Truncated label
     */
    truncateLabel(label) {
      if (!label) return '';
      const labelStr = String(label);
      const maxLength = this.isMobile ? 12 : 20;
      if (labelStr.length <= maxLength) return labelStr;
      return `${labelStr.substring(0, maxLength - 3)}...`;
    },

    /**
     * Wrap label for desktop display with line breaks
     * @param {any} label - The label to wrap
     * @returns {string} Wrapped label
     */
    wrapLabel(label) {
      if (!label) return '';
      const labelStr = String(label);
      const maxLength = 20;
      if (labelStr.length <= maxLength) return labelStr;

      const words = labelStr.split(' ');
      const lines = [];
      let currentLine = '';

      words.forEach((word) => {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        if (testLine.length <= maxLength) {
          currentLine = testLine;
        } else if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          lines.push(word);
        }
      });

      if (currentLine) {
        lines.push(currentLine);
      }

      return lines.join('\n');
    },

    getChartRawLabelAtIndex(dataIndex, datasetIndex = 0) {
      if (dataIndex == null || dataIndex < 0) return null;
      const labels = this.chartConfig?.data?.labels;
      if (labels?.[dataIndex] != null && labels[dataIndex] !== '') {
        return labels[dataIndex];
      }
      const ds = this.chartConfig?.data?.datasets?.[datasetIndex];
      const point = ds?.data?.[dataIndex];
      if (point != null && typeof point === 'object' && point.x != null) {
        return point.x;
      }
      return null;
    },

    formatWeekTooltipTitle(rawLabel) {
      if (!rawLabel) return '';
      const date = new Date(rawLabel);
      if (Number.isNaN(date.getTime())) return rawLabel;
      return formatLocaleDate(date, this.$i18n?.locale);
    },

    getFullLabel(truncatedLabel) {
      // Find the original full label from the chart data
      if (!this.chartConfig?.data?.labels) return truncatedLabel;

      // Look for the original label that matches the truncated one
      const originalLabel = this.chartConfig.data.labels.find((label) => {
        const fullLabel = this.getDimensionLabel(label);
        const truncated = this.truncateLabel(fullLabel);
        return truncated === truncatedLabel;
      });

      if (originalLabel) {
        return this.getDimensionLabel(originalLabel);
      }

      return truncatedLabel;
    },

    /**
     * Get translated Likert label for Y-axis.
     * When useLikertLineScale: 0–4 (Strongly disagree → Strongly agree). Otherwise 1–5.
     * @param {number} value - Integer 0–4 or 1–5 depending on chart mode
     * @returns {string} Translated label
     */
    getLikertYAxisLabel(value) {
      const keyMap04 = {
        0: 'SliderQuestion.datalistOptions.stronglyDisagree',
        1: 'SliderQuestion.datalistOptions.disagree',
        2: 'SliderQuestion.datalistOptions.indifferent',
        3: 'SliderQuestion.datalistOptions.agree',
        4: 'SliderQuestion.datalistOptions.stronglyAgree',
      };
      const keyMap15 = {
        1: 'SliderQuestion.datalistOptions.stronglyDisagree',
        2: 'SliderQuestion.datalistOptions.disagree',
        3: 'SliderQuestion.datalistOptions.indifferent',
        4: 'SliderQuestion.datalistOptions.agree',
        5: 'SliderQuestion.datalistOptions.stronglyAgree',
      };
      const keyMap = this.useLikertLineScale ? keyMap04 : keyMap15;
      const key = keyMap[value];
      return key ? this.$t(key) : String(value);
    },

    /**
     * Format numeric value for tooltip: show number and Likert label.
     * When useLikertLineScale, value is 0–4; otherwise 1–5.
     * @param {number} value - Raw value (may be decimal)
     * @returns {string} Formatted string
     */
    formatLikertTooltipValue(value) {
      if (value == null || Number.isNaN(value)) return '';
      const is04 = this.useLikertLineScale;
      const num = is04 ? Number(value) + 1 : Number(value);
      const minV = is04 ? 0 : 1;
      const maxV = is04 ? 4 : 5;
      const rounded = Math.round(num);
      /* const label = this.getLikertYAxisLabel(Math.min(maxV, Math.max(minV, rounded)));
      if (Number.isInteger(num) && num >= minV && num <= maxV) {
        return `${num.toFixed(2)} (${label})`;
      }
      const lower = Math.floor(Math.min(maxV, Math.max(minV, num)));
      const upper = Math.min(maxV, lower + 1);
      if (lower === upper) return `${num.toFixed(2)} (${label})`;
      const lowerLabel = this.getLikertYAxisLabel(lower);
      const upperLabel = this.getLikertYAxisLabel(upper);
      const between = this.$t('LineChartComponent.likertBetween', [lowerLabel, upperLabel]); */
      return `${num.toFixed(2)}`;
    },

    /**
     * Get translated dimension label for display purposes
     * @param {string} dimensionValue - The dimension value
     * @returns {string} The translated label
     */
    getDimensionLabel(dimensionValue) {
      const dimension = this.dimensions.find((d) => d.value === dimensionValue);
      const locale = this.$i18n?.locale || 'en';
      return dimension?.translation?.[locale] || dimensionValue;
    },

    /**
     * Get chart data with translated labels
     * @param {Object} chartConfig - The chart configuration
     * @returns {Object} Chart data with translated labels
     */
    getTranslatedChartData(chartConfig) {
      if (!chartConfig || !chartConfig.data) {
        return chartConfig;
      }

      const rawLabels = chartConfig.data.labels || [];
      if (!rawLabels.length && !chartConfig.data.datasets?.some((ds) => ds.data?.length)) {
        return chartConfig.data;
      }

      // Translate the labels
      let translatedLabels = rawLabels
        .map((label) => {
          if (isIsoDateChartLabel(label)) {
            return formatLocaleDate(label, this.$i18n?.locale);
          }
          return this.getDimensionLabel(label);
        });

      // Apply label truncation for non-date labels
      translatedLabels = translatedLabels.map((label, idx) => {
        const raw = rawLabels[idx];
        if (isIsoDateChartLabel(raw)) {
          return label;
        }
        return this.truncateLabel(label);
      });

      // For Likert line chart: map 1–5 → 0–4 so Strongly disagree = 0, scale -1 to 5
      const mapLikertTo04 = (v) => {
        if (v == null || Number.isNaN(Number(v))) return v;
        const n = Number(v);
        return n >= 1 && n <= 5 ? n - 1 : v;
      };
      // Time-series data is array of { x, y }; only y must be transformed (team line charts)
      const mapLikertData = (data) => {
        if (!Array.isArray(data)) return data;
        return data.map((item) => {
          if (item != null && typeof item === 'object' && 'y' in item) {
            return { ...item, y: mapLikertTo04(item.y) };
          }
          return mapLikertTo04(item);
        });
      };

      const result = {
        ...chartConfig.data,
        labels: translatedLabels,
        datasets: chartConfig.data.datasets.map((dataset) => ({
          ...dataset,
          data: this.useLikertLineScale && Array.isArray(dataset.data)
            ? mapLikertData(dataset.data)
            : dataset.data,
          backgroundColor: dataset.backgroundColor?.startsWith('#')
            ? dataset.backgroundColor
            : `#${dataset.backgroundColor}`,
          borderColor: dataset.borderColor?.startsWith('#')
            ? dataset.borderColor
            : `#${dataset.borderColor}`,
        })),
      };
      return result;
    },
  },
};
</script>

<style scoped>
/* Component-specific styles if needed */
</style>
